import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, ArrowLeft, ShoppingCart, Package, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function RegisterPage() {
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get('role') || 'buyer');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [abn, setAbn] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [abnChecking, setAbnChecking] = useState(false);
  const [abnResult, setAbnResult] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  const verifyAbn = async () => {
    if (!abn) return;
    setAbnChecking(true);
    setAbnResult(null);
    try {
      const res = await api.get(`/verify/abn/${encodeURIComponent(abn)}`);
      setAbnResult(res.data);
    } catch (err) {
      setAbnResult({ valid_format: false, error: true });
    } finally {
      setAbnChecking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register({ email, password, name, role, company_name: companyName, abn, phone });
      toast.success(`Welcome! Your alias is ${user.alias}`);
      navigate(role === 'seller' ? '/seller' : '/buyer');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-2xl mx-auto px-6 py-8 lg:py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-8" data-testid="register-back-link">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 bg-[#0055FF] flex items-center justify-center rounded-sm">
            <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-heading text-xl font-semibold tracking-tight text-gray-900">LeadXchange</span>
        </div>

        <h1 className="font-heading text-3xl font-semibold tracking-tight text-gray-900">Create your account</h1>
        <p className="mt-2 text-sm text-gray-600">
          Get an anonymous alias instantly. Your real details stay private and verified by LeadXchange only.
        </p>

        {/* Role selector */}
        <div className="mt-8 grid grid-cols-2 gap-3" data-testid="role-selector">
          <button
            type="button"
            onClick={() => setRole('buyer')}
            className={`p-5 border-2 rounded-lg text-left transition-all ${
              role === 'buyer' ? 'border-[#0055FF] bg-blue-50' : 'border-gray-200 hover:border-gray-300'
            }`}
            data-testid="role-buyer-btn"
          >
            <ShoppingCart className={`w-6 h-6 mb-2 ${role === 'buyer' ? 'text-[#0055FF]' : 'text-gray-400'}`} />
            <div className="font-heading text-base font-semibold text-gray-900">I want to Buy Leads</div>
            <div className="text-xs text-gray-500 mt-1 font-mono">Alias: LX-B-XXXX</div>
          </button>
          <button
            type="button"
            onClick={() => setRole('seller')}
            className={`p-5 border-2 rounded-lg text-left transition-all ${
              role === 'seller' ? 'border-[#0055FF] bg-blue-50' : 'border-gray-200 hover:border-gray-300'
            }`}
            data-testid="role-seller-btn"
          >
            <Package className={`w-6 h-6 mb-2 ${role === 'seller' ? 'text-[#0055FF]' : 'text-gray-400'}`} />
            <div className="font-heading text-base font-semibold text-gray-900">I want to Sell Leads</div>
            <div className="text-xs text-gray-500 mt-1 font-mono">Alias: LX-S-XXXX</div>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="register-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-gray-700">Full Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5 h-11" data-testid="register-name-input" />
            </div>
            <div>
              <Label htmlFor="company" className="text-sm font-medium text-gray-700">Company Name</Label>
              <Input id="company" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required className="mt-1.5 h-11" data-testid="register-company-input" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="abn" className="text-sm font-medium text-gray-700">ABN</Label>
              <div className="mt-1.5 flex gap-2">
                <Input
                  id="abn"
                  value={abn}
                  onChange={(e) => { setAbn(e.target.value); setAbnResult(null); }}
                  placeholder="11-digit ABN"
                  className="h-11 flex-1"
                  data-testid="register-abn-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={verifyAbn}
                  disabled={!abn || abnChecking}
                  className="h-11 border-gray-300"
                  data-testid="verify-abn-btn"
                >
                  {abnChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                </Button>
              </div>
              {abnResult && (
                <div className={`mt-2 flex items-center gap-2 text-xs px-2 py-1.5 rounded border ${
                  abnResult.valid_format
                    ? 'bg-green-50 text-green-800 border-green-200'
                    : 'bg-red-50 text-red-800 border-red-200'
                }`} data-testid="abn-verify-result">
                  {abnResult.valid_format ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      <span>Valid ABN format · Checksum OK</span>
                      {abnResult.abr_lookup && <span className="ml-1 font-mono">· {abnResult.abr_lookup.company_name}</span>}
                    </>
                  ) : (
                    <>
                      <X className="w-3.5 h-3.5" />
                      <span>Invalid ABN — checksum does not match</span>
                    </>
                  )}
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="phone" className="text-sm font-medium text-gray-700">Phone</Label>
              <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+61..." className="mt-1.5 h-11" data-testid="register-phone-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 h-11" data-testid="register-email-input" />
          </div>
          <div>
            <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="mt-1.5 h-11" data-testid="register-password-input" />
            <p className="mt-1 text-xs text-gray-500">Minimum 8 characters</p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-xs text-gray-600">
            <span className="font-medium text-gray-900">Your information stays private.</span> Only LeadXchange will see your real company name, ABN, and contact details. On the public marketplace you will appear only as your assigned alias.
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-[#0055FF] hover:bg-[#0044CC] text-white"
            data-testid="register-submit-btn"
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </Button>

          <p className="text-sm text-gray-600 text-center">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-[#0055FF] hover:underline" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
